// Study payload schema (Phase 12a). The working-state JSON shape that
// the Designer authors and the runner consumes — see
// `docs/STUDY_DESIGNER.md` §5.2 and `schema/STORAGE.md` §3.
//
// Implicit version 0: no `version` field today. First explicit bump
// adds the field plus a migrator. Loaders reject unknown `version`
// values; that gate is in `validateStudyPayload` below.
//
// We use TypeBox's runtime `Value` module rather than ajv so the
// validator has no extra dependency footprint. TypeBox 0.32 already
// ships in the workspace.

import { FormatRegistry, Type, type Static, type TSchema } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { mintSlug, slugifyName } from './slug.ts';

// TypeBox's Value module does not ship JSON-Schema format validators
// out of the box. We register the two we use so our schema can
// declare `format: 'date-time' | 'uri'` and runtime Check() will
// accept them. These are intentionally loose: Designer-emitted
// timestamps come from new Date().toISOString() (always valid) and
// user-entered URIs are validated at the input field with stronger
// checks; this gate just rejects obvious garbage on import.
if (!FormatRegistry.Has('date-time')) {
  FormatRegistry.Set(
    'date-time',
    (value) => typeof value === 'string' && !Number.isNaN(Date.parse(value)),
  );
}
if (!FormatRegistry.Has('uri')) {
  FormatRegistry.Set('uri', (value) => {
    if (typeof value !== 'string') return false;
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  });
}

// --- step kinds ------------------------------------------------------

export const STEP_KINDS = [
  'instruction',
  'survey',
  'task',
  'prompt',
  'wait',
  'branch',
  'sync',
] as const;
export type StepKind = (typeof STEP_KINDS)[number];

// --- edge predicates -------------------------------------------------
// `when` is a tagged union. New predicate kinds are additive.

const PredicateComplete = Type.Object({
  type: Type.Literal('complete'),
});

const PredicateResponseEquals = Type.Object({
  type: Type.Literal('responseEquals'),
  question: Type.String({ minLength: 1 }),
  value: Type.Unknown(),
});

const PredicateTaskCompleted = Type.Object({
  type: Type.Literal('taskCompleted'),
  taskId: Type.String({ minLength: 1 }),
});

const PredicateManual = Type.Object({
  type: Type.Literal('manual'),
  advanceableBy: Type.Optional(
    Type.Array(Type.Union([Type.Literal('facilitator'), Type.Literal('observer')]), {
      minItems: 1,
      uniqueItems: true,
    }),
  ),
});

const PredicateTimer = Type.Object({
  type: Type.Literal('timer'),
  msFromStepEntry: Type.Optional(Type.Integer({ minimum: 0 })),
  atSessionTimeMs: Type.Optional(Type.Integer({ minimum: 0 })),
});

export const EdgePredicateSchema = Type.Union([
  PredicateComplete,
  PredicateResponseEquals,
  PredicateTaskCompleted,
  PredicateManual,
  PredicateTimer,
]);
export type EdgePredicate = Static<typeof EdgePredicateSchema>;

// --- library element shapes -----------------------------------------

const LibrarySurveyJson = Type.Unknown(); // opaque SurveyJS JSON; SurveyJS owns its own schema

const LibraryInstruction = Type.String(); // markdown source

// Task success criteria — the rule that produces a `task.complete`
// envelope at runtime. A tagged union so authoring stays explicit:
//   manual           facilitator clicks Done in the dashboard
//   participant      participant clicks Done on the kiosk
//   timer            auto-fires after `durationSec` elapses
//   observerMarker   any observer marker carrying the matching `label`
//
// `manual.advanceableBy` mirrors the edge predicate of the same name
// so a study can opt observers into the Done-button gate without
// touching any wire surface.

const TaskCriterionManual = Type.Object({
  type: Type.Literal('manual'),
  advanceableBy: Type.Optional(
    Type.Array(Type.Union([Type.Literal('facilitator'), Type.Literal('observer')]), {
      minItems: 1,
      uniqueItems: true,
    }),
  ),
});

const TaskCriterionParticipant = Type.Object({
  type: Type.Literal('participant'),
});

const TaskCriterionTimer = Type.Object({
  type: Type.Literal('timer'),
  durationSec: Type.Number({ exclusiveMinimum: 0 }),
});

const TaskCriterionObserverMarker = Type.Object({
  type: Type.Literal('observerMarker'),
  // Matches the `markerType` field on the wire-level
  // `observer.marker` envelope (see schema/messages/observer.ts).
  markerType: Type.String({ minLength: 1 }),
});

export const TaskSuccessCriterionSchema = Type.Union([
  TaskCriterionManual,
  TaskCriterionParticipant,
  TaskCriterionTimer,
  TaskCriterionObserverMarker,
]);
export type TaskSuccessCriterion = Static<typeof TaskSuccessCriterionSchema>;

const LibraryTask = Type.Object({
  name: Type.String({ minLength: 1 }),
  // Markdown body shown on the participant kiosk under the task title.
  description: Type.Optional(Type.String()),
  // Markdown body shown only on the facilitator dashboard. Useful for
  // "what to look for" or operator hints that shouldn't reach the
  // participant.
  instructions: Type.Optional(Type.String()),
  // Duration hint surfaced in the kiosk + dashboard. The `timer`
  // criterion ignores this field (it carries its own `durationSec`);
  // for other criteria it is purely informational.
  estimatedDurationSec: Type.Optional(Type.Number({ minimum: 0 })),
  successCriterion: TaskSuccessCriterionSchema,
  notes: Type.Optional(Type.String()),
});
export type LibraryTaskValue = Static<typeof LibraryTask>;

const LibraryPrompt = Type.Object({
  name: Type.String({ minLength: 1 }),
  text: Type.String(),
});

export const StudyLibrarySchema = Type.Object({
  surveys: Type.Record(Type.String({ minLength: 1 }), LibrarySurveyJson),
  instructions: Type.Record(Type.String({ minLength: 1 }), LibraryInstruction),
  tasks: Type.Record(Type.String({ minLength: 1 }), LibraryTask),
  prompts: Type.Record(Type.String({ minLength: 1 }), LibraryPrompt),
});
export type StudyLibrary = Static<typeof StudyLibrarySchema>;

// --- participant role, step, edge, lane ----------------------------

// Logical capture role authored at design time. The Designer adds one
// of these per camera/mic/screen the participant role needs (e.g. a
// role might have a "face-cam", a "lapel-mic", and a "main-screen").
// Each role resolves to a real device id at session-start time, when
// the dashboard renders a binding picker against the bound media
// node's `device.list`. The `id` is stable across sessions so
// persistent bindings (controller_capture_bindings) can survive.
export const CaptureProfileItemSchema = Type.Object({
  id: Type.String({ minLength: 1 }),
  kind: Type.Union([
    Type.Literal('camera'),
    Type.Literal('microphone'),
    Type.Literal('screen'),
  ]),
  label: Type.String({ minLength: 1 }),
  // Optional last-known binding. The Designer authors capture roles
  // abstractly; at session-start the dashboard's binding picker
  // resolves each one to a real device on a media node and may write
  // the resolution back into the exported study so a downstream
  // standalone build can show "this camera was Alice's webcam on
  // agent-2" without a live controller. Absent = unassigned.
  agentId: Type.Optional(Type.String({ minLength: 1 })),
  // Opaque, mediad-minted device tag (e.g. "camera-7h3rj9"). The
  // OS-native handle stays inside mediad — see
  // schema/messages/device.ts.
  deviceTag: Type.Optional(Type.String({ minLength: 1 })),
});
export type CaptureProfileItem = Static<typeof CaptureProfileItemSchema>;

export const CaptureProfileSchema = Type.Object({
  items: Type.Array(CaptureProfileItemSchema),
});
export type CaptureProfile = Static<typeof CaptureProfileSchema>;

export const ParticipantRoleSchema = Type.Object({
  id: Type.String({ minLength: 1 }),
  name: Type.String({ minLength: 1 }),
  color: Type.Optional(Type.String()), // hex from a fixed palette; absent = auto-pick
  // Researcher-facing note explaining what this role is for. Surfaces in
  // the Designer's Overview tab and on the participant kiosk's role
  // picker so observers binding agents know which role to claim.
  description: Type.Optional(Type.String()),
  // Logical capture roles for this participant role (cameras / mics /
  // screens). Optional so older study payloads stay valid; absence
  // means "no capture for this role".
  capture: Type.Optional(CaptureProfileSchema),
});
export type ParticipantRole = Static<typeof ParticipantRoleSchema>;

/**
 * One entry in a study's participant-code ledger. The Designer's Plan
 * tab manages these; the Observer's Survey-PDF + Run cards pick from
 * them when binding a download or a seat. Soft-delete by design:
 * `removedAt` keeps the audit trail intact (a returned PDF that still
 * carries the code attributes correctly).
 *
 * **No names live on these entries** — participants are anonymous in
 * the evaluation/analysis surfaces. Researchers keep the
 * code → human mapping out-of-band (the Consent Form PDFs serve
 * as the legal-name record; they are not re-uploaded to the study).
 */
/**
 * "Who done it" stamp for audit columns. Captured from the
 * operator's locally-stored identity at the moment a row is
 * written — userId is the stable installation slug, displayName +
 * pin disambiguate two researchers with the same first name in
 * the audit view ("Alice (1234)" vs "Alice (5678)").
 *
 * The PIN is intentionally non-sensitive — the UI tells operators
 * to pick something memorable they don't mind being visible on
 * screen (last 4 of their phone, a birthday, a favorite 4-digit
 * prime). It is NOT an authentication factor.
 */
export const ActorStampSchema = Type.Object({
  userId: Type.String({ minLength: 1 }),
  displayName: Type.String({ minLength: 1 }),
  pin: Type.String({ pattern: '^\\d{4}$' }),
});
export type ActorStamp = Static<typeof ActorStampSchema>;

export const ParticipantIdEntrySchema = Type.Object({
  /** The 3–6 char [A-Z0-9] code. Stored uppercased. */
  code: Type.String({ minLength: 3, maxLength: 6, pattern: '^[A-Z0-9]{3,6}$' }),
  /** ISO timestamp when added to the ledger. */
  addedAt: Type.String({ format: 'date-time' }),
  /** ISO timestamp when soft-deleted; absent = active. */
  removedAt: Type.Optional(Type.String({ format: 'date-time' })),
  /** Operator who added the row. Optional for backwards-compat. */
  addedBy: Type.Optional(ActorStampSchema),
  /**
   * ISO timestamp when an operator clicked "I attest that this
   * participant has consented" on the participants table. The legal
   * record is the signed paper consent form (see ConsentFormSchema);
   * this field is the digital-side acknowledgement that the paper
   * exists. Absent = not yet attested. Cleared if the operator
   * un-attests (e.g. attested wrong row).
   */
  consentedAt: Type.Optional(Type.String({ format: 'date-time' })),
  /** Operator who clicked attest. Paired with consentedAt. */
  consentAttestedBy: Type.Optional(ActorStampSchema),
});
export type ParticipantIdEntry = Static<typeof ParticipantIdEntrySchema>;

/**
 * Per-study consent-form body, edited in the Designer's Plan tab and
 * rendered into a download-N-page PDF on the Observer side (one page
 * per active participant ID; each page carries the code top-right
 * plus name, date, and signature fields the participant fills in by
 * pen). The signed paper is the legal record kept by the researcher;
 * nothing about it round-trips back into the study, so participants
 * stay anonymous in the digital plane.
 */
export const ConsentFormSchema = Type.Object({
  /** Markdown body. Empty string is valid — the form falls back to a default. */
  content: Type.String(),
  /** ISO timestamp of the last edit; used as a freshness hint. */
  updatedAt: Type.Optional(Type.String({ format: 'date-time' })),
});
export type ConsentForm = Static<typeof ConsentFormSchema>;

const StepBranch = Type.Object({
  to: Type.String({ minLength: 1 }),
  when: EdgePredicateSchema,
  label: Type.Optional(Type.String()),
});

export const StepSchema = Type.Object({
  id: Type.String({ minLength: 1 }),
  kind: Type.Union(STEP_KINDS.map((k) => Type.Literal(k))),
  ref: Type.Optional(Type.String({ minLength: 1 })),
  participantRoleIds: Type.Array(Type.String({ minLength: 1 }), { uniqueItems: true }),
  label: Type.Optional(Type.String()),
  externalUrl: Type.Optional(Type.String({ format: 'uri' })),
  branches: Type.Optional(Type.Array(StepBranch)),
});
export type Step = Static<typeof StepSchema>;

export const EdgeSchema = Type.Object({
  from: Type.String({ minLength: 1 }),
  to: Type.String({ minLength: 1 }),
  when: EdgePredicateSchema,
});
export type Edge = Static<typeof EdgeSchema>;

// --- the study payload --------------------------------------------

export const StudyPayloadSchema = Type.Object({
  id: Type.String({ minLength: 1 }),
  name: Type.String({ minLength: 1 }),
  // Human-readable slug, separate from the opaque `id`. Used wherever
  // a short label is friendlier than the prefixed nanoid (PDF headers,
  // export filenames, future URL slugs). Optional for backwards-compat
  // with studies saved before the field existed; the Designer fills it
  // in on next save.
  tag: Type.Optional(Type.String({ minLength: 1 })),
  description: Type.Optional(Type.String()),
  createdAt: Type.String({ format: 'date-time' }),
  updatedAt: Type.String({ format: 'date-time' }),
  forkOf: Type.Union([Type.Null(), Type.String({ minLength: 1 })]),

  participantRoles: Type.Array(ParticipantRoleSchema),
  // Per-study participant-code ledger. Optional for backwards-compat
  // with studies authored before this field existed; the Designer's
  // Plan tab + the participant-code modal write it on first use.
  participantIds: Type.Optional(Type.Array(ParticipantIdEntrySchema)),
  // Consent-form body the Plan tab edits + the Observer Surveys tab
  // prints. Optional — studies without one fall back to a built-in
  // placeholder when the operator triggers a download.
  consentForm: Type.Optional(ConsentFormSchema),
  library: StudyLibrarySchema,
  steps: Type.Record(Type.String({ minLength: 1 }), StepSchema),
  edges: Type.Array(EdgeSchema),
  lanes: Type.Record(
    Type.String({ minLength: 1 }),
    Type.Array(Type.String({ minLength: 1 })),
  ),
  roots: Type.Array(Type.String({ minLength: 1 })),
});
export type StudyPayload = Static<typeof StudyPayloadSchema>;

// --- history sidecar ----------------------------------------------

const ImmerOp = Type.Object({
  op: Type.Union([Type.Literal('add'), Type.Literal('replace'), Type.Literal('remove')]),
  path: Type.Array(Type.Union([Type.String(), Type.Integer()])),
  value: Type.Optional(Type.Unknown()),
});

export const HistoryEntrySchema = Type.Object({
  ts: Type.String({ format: 'date-time' }),
  author: Type.String(),
  description: Type.String(),
  patches: Type.Array(ImmerOp),
  inverse: Type.Array(ImmerOp),
});
export type HistoryEntry = Static<typeof HistoryEntrySchema>;

export const StudyHistorySchema = Type.Array(HistoryEntrySchema);
export type StudyHistory = Static<typeof StudyHistorySchema>;

// --- preset bundle ------------------------------------------------

export const PresetBundleSchema = Type.Object({
  name: Type.String({ minLength: 1 }),
  description: Type.Optional(Type.String()),
  createdAt: Type.String({ format: 'date-time' }),
  library: StudyLibrarySchema,
});
export type PresetBundle = Static<typeof PresetBundleSchema>;

// --- validation helpers -------------------------------------------

export interface ValidationError {
  path: string;
  message: string;
}

export interface ValidationResult<T> {
  ok: boolean;
  value: T | null;
  errors: ValidationError[];
}

function runCheck<T extends TSchema>(
  schema: T,
  candidate: unknown,
): ValidationResult<Static<T>> {
  if (Value.Check(schema, candidate)) {
    return { ok: true, value: candidate as Static<T>, errors: [] };
  }
  const errors: ValidationError[] = [];
  for (const e of Value.Errors(schema, candidate)) {
    errors.push({ path: e.path || '/', message: e.message });
    if (errors.length >= 25) break; // cap; the first batch is enough to surface to the user
  }
  return { ok: false, value: null, errors };
}

export function validateStudyPayload(candidate: unknown): ValidationResult<StudyPayload> {
  // Reject any future `version` field ahead of the implicit-v0 migration.
  if (candidate && typeof candidate === 'object' && 'version' in (candidate as object)) {
    const version = (candidate as { version: unknown }).version;
    if (version !== undefined && version !== 0) {
      return {
        ok: false,
        value: null,
        errors: [
          {
            path: '/version',
            message: `unknown study payload version ${String(version)}; this Designer build only understands implicit v0`,
          },
        ],
      };
    }
  }
  return runCheck(StudyPayloadSchema, candidate);
}

export function validateStudyHistory(candidate: unknown): ValidationResult<StudyHistory> {
  return runCheck(StudyHistorySchema, candidate);
}

export function validatePresetBundle(candidate: unknown): ValidationResult<PresetBundle> {
  return runCheck(PresetBundleSchema, candidate);
}

// --- empty / starter payload --------------------------------------
// First-run experience: a brand-new study seeds with one participant
// role and one instruction step so the Designer never opens onto a
// blank canvas (see §6.9 "Empty states are tutorial").

/**
 * Normalize a user-typed study tag to the slug character set. Pure
 * sanitization — never appends a nano suffix, because the user-edit
 * path should respect what the user wrote. Falls back to `study`
 * when the input has no alphanumerics.
 */
export function slugifyStudyTag(name: string): string {
  return slugifyName(name, { fallback: 'study' });
}

/**
 * Mint a fresh study tag for a new (or legacy-backfilled) study.
 * Result is `<name-derived>-<nano4>` — see ./slug.ts.
 */
export function mintStudyTag(name: string): string {
  return mintSlug(name, { fallback: 'study' });
}

/**
 * Fill in fields that older payloads may be missing so the rest of
 * the app can assume they're present. Returns the same object when
 * nothing needed backfilling so callers can detect "did this need
 * migration?" with a reference check.
 */
/**
 * Backfill optional fields on payloads that pre-date their addition.
 * Today the only such field is `tag`: studies saved before slugs
 * existed need one minted on next read.
 *
 * Importantly, we don't mint a tag from an empty name — that would
 * land `study-<nano>` (the fallback) on disk for every brand-new
 * study, defeating the "wait for the operator to title the study,
 * then derive the slug from THAT" flow. The empty-name case is
 * handled by the editor on first non-empty rename instead.
 */
export function normalizeStudyPayload(payload: StudyPayload): StudyPayload {
  if (payload.tag && payload.tag.length > 0) return payload;
  if (!payload.name) return payload;
  return { ...payload, tag: mintStudyTag(payload.name) };
}

export function newStudyPayload(opts: {
  id: string;
  /** Empty string defers the slug-mint until the operator types a
   *  real name. Empty-name studies render in the list with an
   *  "Untitled" placeholder; the first non-empty rename mints the
   *  tag from the new name. */
  name: string;
  now?: string;
}): StudyPayload {
  const now = opts.now ?? new Date().toISOString();
  return {
    id: opts.id,
    name: opts.name,
    // Defer the slug until there's a name to derive it from.
    // Saving the study with an empty tag is intentional; the
    // editor's commit path mints one on the first non-empty rename.
    tag: opts.name ? mintStudyTag(opts.name) : '',
    description: '',
    createdAt: now,
    updatedAt: now,
    forkOf: null,
    participantRoles: [{ id: 'primary-user', name: 'Primary user' }],
    library: {
      surveys: {},
      instructions: {
        welcome: 'Welcome! This is your first step. Edit me from the Library.',
      },
      tasks: {},
      prompts: {},
    },
    steps: {
      'step-welcome': {
        id: 'step-welcome',
        kind: 'instruction',
        ref: 'welcome',
        participantRoleIds: ['primary-user'],
        label: 'Welcome',
      },
    },
    edges: [],
    lanes: { 'primary-user': ['step-welcome'] },
    roots: ['step-welcome'],
  };
}
